import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { Frame } from "@/components/site/Frame";
import { Marquee } from "@/components/site/Marquee";
import { CTA } from "@/components/site/CTA";
import { SERVICES, MARQUEE } from "@/data/content";

const STEPS = [
  { n: "01", t: "Brief & survey", d: "We visit, listen and measure, so the design starts from how the building really is." },
  { n: "02", t: "Design & approvals", d: "Concept options, planning and building regulations drawings coordinated with cost." },
  { n: "03", t: "Build", d: "A realistic programme, the right sequence of work and a managed, safe site." },
  { n: "04", t: "Handover", d: "Snagging, sign-off and a clear handover, with someone accountable throughout." },
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="services-page">
      <PageHero
        testId="services-hero"
        eyebrow="Services"
        lines={["From the first", "drawing to the", "final finish."]}
        intro="Architectural and build services for homes and commercial spaces across Preston and the North West."
      />

      <section className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-28 sm:pb-36 space-y-px bg-[#262b38] border-y border-[#262b38]">
        {SERVICES.map((s, i) => (
          <article key={s.id} id={s.id} data-testid={`service-${s.id}`} className="bg-[#0c0e12] grid lg:grid-cols-12 gap-10 py-14 sm:py-20 scroll-mt-24">
            <Reveal className="lg:col-span-5">
              <Frame src={s.img} alt={s.title} className="aspect-[4/3]" label={s.code} />
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7 lg:pl-10 flex flex-col justify-center">
              <span className="font-mono text-xs text-[#d96b27]">{String(i + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}</span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-3">{s.title}</h2>
              <p className="text-[#a7aebb] text-base md:text-lg leading-relaxed mt-6 max-w-2xl">{s.text}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-[#c9ced8] border border-[#262b38] px-4 py-3">
                    <span className="w-1.5 h-1.5 bg-[#d96b27]" /> {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </article>
        ))}
      </section>

      <Marquee items={MARQUEE} />

      <section data-testid="services-process" className="mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-36">
        <Reveal>
          <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight mb-16">How a project runs</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#262b38] border border-[#262b38]">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className="bg-[#0c0e12] p-8 min-h-[260px] flex flex-col justify-between hover:bg-[#14171f] transition-colors">
              <span className="font-display text-6xl font-extrabold outline-text">{s.n}</span>
              <div>
                <h3 className="font-display text-2xl font-bold">{s.t}</h3>
                <p className="text-[#a7aebb] mt-3 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTA />
    </div>
  );
}
